import { useCallback } from 'react';

import { useEffectOnce } from 'src/lib/hooks';
import { usePopups } from 'src/lib/popups';
import { useCipEvents, useErrorHandler } from 'src/services';
import { useCustomerActions, usePerformanceActions } from 'src/store';
import { useResourceActions } from 'src/store/resource';

import { AddFileResourcePopup } from './AddFileResourcePopup';

export const ADD_FILE_RESOURCE_POPUP = Symbol('ADD_FILE_RESOURCE');

export const useAddFileResourcePopup = () => {
  const popups = usePopups();
  const performanceActions = usePerformanceActions();
  const resourceActions = useResourceActions();
  const customerActions = useCustomerActions();
  const cipEvents = useCipEvents();
  const handleError = useErrorHandler();

  useEffectOnce(() => {
    popups.registerPopup(ADD_FILE_RESOURCE_POPUP, AddFileResourcePopup);
  });

  return useCallback(
    ({ type, brandVoiceId, onCreateSuccess, onConnectSuccess, allowReplace = false }) => {
      return new Promise((resolve) => {
        popups.openPopup(ADD_FILE_RESOURCE_POPUP, {
          onClose: () => {
            popups.closePopup(ADD_FILE_RESOURCE_POPUP);
            resolve();
          },
          onSubmit: async ({ resource }) => {
            try {
              if (!allowReplace) {
                popups.closePopup(ADD_FILE_RESOURCE_POPUP);
              }
              const result = await performanceActions.createResourceAccount({
                csvFile: resource,
                channel: type,
                brandVoiceId,
                onCreateSuccess,
                onConnectSuccess,
              });
              cipEvents.trackAddResource(type, result.name);

              // Refresh customer limits and rows allocation, do not wait for result
              resourceActions.queryWorkspaceResources().catch(handleError);
              customerActions.refreshCurrentCustomerFeatureLimit();
              customerActions.refreshCurrentCustomerRowsAllocation();

              if (allowReplace) {
                popups.closePopup(ADD_FILE_RESOURCE_POPUP);
              }
              resolve({ channel: result.dataSource, ...result });
            } catch (error) {
              handleError(error);
              resolve();
            }
          },
          type,
          allowReplace,
        });
      });
    },
    [popups, performanceActions, cipEvents, resourceActions, handleError, customerActions]
  );
};
